import React from "react";

/**
 * QENVA · CrossCheck Score Gauge.
 *
 * Displays the composite CrossCheck score [0–100] with a colored ring
 * matching the band (excellent / good / caution / at_risk). Used as
 * the `leadingSlot` of the Guardian Portfolio Panel on the CrossCheck
 * page.
 *
 * Props
 * ─────
 *   value : number 0–100
 *   band  : "excellent" | "good" | "caution" | "at_risk"
 *   label : string under the gauge
 *   bandLabel : string above the gauge
 */
const BAND_COLOR = {
  excellent: { ring: "#10B981", text: "text-emerald-300" },
  good:      { ring: "#3DD68C", text: "text-emerald-200" },
  caution:   { ring: "#F59E0B", text: "text-amber-200" },
  at_risk:   { ring: "#EF4444", text: "text-rose-200" },
};

export default function CrossCheckScoreGauge({ value = 0, band = "caution", label, bandLabel, testId = "crosscheck-score-gauge" }) {
  const v = Math.max(0, Math.min(100, value));
  const c = BAND_COLOR[band] || BAND_COLOR.caution;
  // Conic gradient — the ring fills counter-clockwise from 12 o'clock
  const angle = v * 3.6; // 360deg per 100
  const ringStyle = {
    background: `conic-gradient(${c.ring} ${angle}deg, rgba(255,255,255,0.10) ${angle}deg)`,
  };

  return (
    <div className="flex flex-col items-start gap-2" data-testid={testId}>
      {bandLabel && (
        <p className={`text-[10px] uppercase tracking-[0.18em] font-bold ${c.text}`} data-testid={`${testId}-band`}>
          {bandLabel}
        </p>
      )}
      <div className="relative w-[150px] h-[150px] rounded-full grid place-items-center" style={ringStyle}>
        <div className="absolute inset-[8px] rounded-full bg-qenva-navy/95 grid place-items-center">
          <div className="text-center">
            <p className="font-display font-bold text-white text-[40px] leading-none tabular-nums">{v}</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-semibold mt-0.5">/100</p>
          </div>
        </div>
      </div>
      {label && (
        <p className="text-[12px] font-semibold text-white/80 max-w-[180px]" data-testid={`${testId}-label`}>
          {label}
        </p>
      )}
    </div>
  );
}
